import { Button, Space, Typography } from "antd";
import { FC, useCallback, useContext, useMemo } from "react";
import {
  Inline,
  useAppContext,
  useClientContext,
  useInlineContext,
  useNavigation,
  View,
} from "@data-root/react";

import {
  SchemaItemProps,
  useAsyncCallback,
  useField,
  UseRowContext,
} from "@data-root/react";
import {
  RefSchemaItem,
  RowWithEmbeddedMetadata,
  View as IView,
} from "@data-root/types";
import { DeckRows } from "@data-root/plugin-view-deck";
import { useQueries } from "@tanstack/react-query";
import { useInlineFilter } from "../../../hooks/useInlineFilter";
import { v4 } from "uuid";

const { Title } = Typography;

export const RefDetailFormPresentation: FC<SchemaItemProps<RefSchemaItem>> = ({
  item,
}) => {
  const { core } = useClientContext();
  const { appSpecifier, getTable } = useAppContext();
  const { navigation } = useNavigation();
  const { view } = useInlineContext();

  const { loaded, name, show, value } = useField(item);

  const refTable = useMemo(() => getTable(item.ref), [getTable, item.ref]);

  const multiple = useMemo(() => item.multiple || false, [item.multiple]);

  const isNullish = useMemo<boolean>(() => {
    if (!loaded) return true;
    const isNull = value === null;
    const isUndefined = !value;
    return isNull || isUndefined;
  }, [loaded, value]);

  const values = useMemo<string[]>(() => {
    if (isNullish) return [];
    return (multiple ? value : [value]) as string[];
  }, [isNullish, multiple, value]);

  const { rows, ready } = useQueries({
    queries: values.map((rowId) => ({
      queryKey: ["row", appSpecifier, item.ref, rowId],
      queryFn: async () => await core.get(appSpecifier, item.ref, rowId),
    })),
    combine: (results) => ({
      rows: results.map((result) => result.data),
      ready: results.every((result) => result.status === "success"),
    }),
  });

  const onClick = useAsyncCallback(
    async (refRow: RowWithEmbeddedMetadata) => {
      const appNavigation = navigation.getParent("app" as any)!;
      appNavigation.navigate(item.detailViewId!, {
        rowId: refRow.id,
      });
    },
    [item.detailViewId, navigation]
  );

  const onEdit = useAsyncCallback(
    async (refRow: RowWithEmbeddedMetadata) => {
      const appNavigation = navigation.getParent("app" as any)!;
      appNavigation.navigate(item.formViewId!, {
        rowId: refRow.id,
      });
    },
    [item.formViewId, navigation]
  );

  const actionIds = useMemo(() => {
    const actionIds: string[] = [];

    if (
      view.customizations &&
      item.id in view.customizations &&
      view.customizations[item.id].actionIds
    ) {
      actionIds.push(...view.customizations[item.id].actionIds);
    } else {
      actionIds.push("EDIT");
    }

    return actionIds;
  }, [item.id, view.customizations]);

  const primaryHeader = useMemo(() => {
    if (
      view.customizations &&
      item.id in view.customizations &&
      view.customizations[item.id].primaryHeader
    ) {
      return view.customizations[item.id].primaryHeader;
    }

    return refTable.label;
  }, [item.id, refTable.label, view.customizations]);

  const secondaryHeader = useMemo(() => {
    if (
      view.customizations &&
      item.id in view.customizations &&
      view.customizations[item.id].secondaryHeader
    ) {
      return view.customizations[item.id].secondaryHeader;
    }

    return refTable.secondaryLabel;
  }, [item.id, refTable.secondaryLabel, view.customizations]);

  const image = useMemo(() => {
    if (
      view.customizations &&
      item.id in view.customizations &&
      view.customizations[item.id].image
    ) {
      return view.customizations[item.id].image;
    }

    return refTable.imageLabel;
  }, [item.id, refTable.imageLabel, view.customizations]);

  const inlineFilter = useMemo(() => {
    if (!view.customizations || !view.customizations[item.id]) return null;

    return view.customizations[item.id].inlineFilter;
  }, [item.id, view.customizations]);

  const {
    values: filterValues,
    filteredRows,
    ready: filterReady,
    filter,
    selectedFilter,
  } = useInlineFilter(item, rows, inlineFilter);

  const inlineView = useMemo<IView>(
    () => ({
      id: `inline:${item.ref}`,
      name: `Inline ${item.ref}`,
      description: `Inline ${item.ref}`,
      type: "Deck",
      tableId: item.ref,
      icon: "TableOutlined",
      options: {
        primaryHeader,
        secondaryHeader,
        image,
        actionIds,
        inlineFilter,
      },
    }),
    [actionIds, image, inlineFilter, item.ref, primaryHeader, secondaryHeader]
  );

  const getContext = useCallback(
    () => ({
      appId: appSpecifier.appId,
      deploymentId: appSpecifier.deploymentId,
      tableId: item.ref,
      // rowId,
      // row,
    }),
    [appSpecifier.appId, appSpecifier.deploymentId, item.ref]
  );

  const containerId = useMemo(() => v4(), []);

  if (!show) return null;

  return (
    <View style={{ width: "100%" }}>
      <Title level={5}>{name}</Title>

      {!!inlineFilter && filterReady && filterValues.length > 0 && (
        <Space style={{ overflowX: "auto", width: "100%", maxWidth: "100%" }}>
          {filterValues.map((value, index) => (
            <Button
              key={index}
              onClick={() => filter(value)}
              type={selectedFilter === value ? "primary" : "default"}
            >
              {value}
            </Button>
          ))}
        </Space>
      )}

      <Inline
        id={containerId}
        height={500}
        table={refTable}
        view={inlineView}
        onClick={onClick}
        onEdit={onEdit}
      >
        <DeckRows
          child
          containerId={containerId}
          getContext={getContext}
          loaded={loaded && ready}
          totalRows={filteredRows.length}
          rows={filteredRows}
          showGroups={false}
        />
      </Inline>
    </View>
  );
};
